import { useId, useRef, useState, useEffect } from 'react'
import styled from 'styled-components'
import { css } from '@/features/shared/styles'
import { normalizeUnitLabel, getUnitLabel } from '@/features/shared/utils/normalizeProduct'
import { type PromotionMetadata } from '@/features/shop/services/Product/types'
import { AddIcon, SubtractIcon, DeleteIcon } from '@shipt/design-system-icons'
import { Body } from '@shipt/design-system-typography'
import { keys } from '@/features/shared/constants/keyCodes'
import { keyframes } from '@/features/shared/styles/effects'
import { ProductQuickAddNote } from '@/features/shop/components/ProductQuantityQuickAdd/ProductQuickAddNote'
import { LoadingIndicator } from '@shipt/design-system-loading'
import {
  getProductCardName,
  getQtyIncrementForProduct,
} from '@/features/shop/components/ProductCard/Utils'
import { useCurrentActiveOrderId } from '@/features/shop/state/SelectedActiveOrder/hooks'
import { trackElementViewed } from '@/features/shared/analytics/element'
import { FlagBadge } from '@/features/shared/elements/Badges'
import { ProductCardFlag } from '@/features/shop/components/ProductCard/ProductCardFlag'
import { useHandleDialog } from '@/features/shared/state/Dialog/useHandleDialog'
import { DynamicVariationsQuickAddModal } from '@/features/shop/components/Modal/VariationsQuickAddModal/DynamicVariationsQuickAddModal'
import { screenSizes } from '@shipt/design-system-themes'
import { useCanShowViewType } from '@/features/shared/services/ViewedElementTracker/hooks'
import { IconButton } from '@shipt/design-system-buttons'
import {
  type OnComplementaryShelfFetch,
  type ProductCardProduct,
} from '@/features/shop/components/ProductCard/types'
import { useAutoOpenFreeItemPromoModal } from '@/features/shop/hooks/useAutoOpenFreeItemPromoModal'
import { useProductCommerceGate } from '@/features/shared/hooks/useProductCommerceGate'

type Props = {
  onAdd: () => boolean | void | Promise<boolean | void>
  onRemove: () => void
  product: ProductCardProduct
  quantity: number
  displayContext?: boolean
  showProductFlag: boolean
  shouldShowPopover?: boolean
  updateListOnly?: boolean
  isUpdatingQuantity?: boolean
  index?: number
  showLargerProducts?: boolean
  onComplementaryShelfFetch?: OnComplementaryShelfFetch
  hideDeleteIcon?: boolean
  preventRemoval?: boolean
  listId?: string
  promotionMetadata?: PromotionMetadata | null
}

export const ProductQuantityQuickAdd = ({
  quantity = 0,
  product,
  displayContext = true,
  onAdd,
  onRemove,
  showProductFlag = false,
  shouldShowPopover = false,
  updateListOnly = false,
  isUpdatingQuantity = false,
  index,
  showLargerProducts = false,
  onComplementaryShelfFetch,
  hideDeleteIcon = false,
  preventRemoval = false,
  listId,
  promotionMetadata,
}: Props) => {
  const id = useId()
  const [showAddNote, setShowAddNote] = useState(false)
  const openStateTimeout = useRef<number | undefined>(undefined)
  const addOpenButton = useRef<HTMLButtonElement>(null)
  const currentOrderId = useCurrentActiveOrderId()
  const orderIsUpdating = Boolean(currentOrderId && isUpdatingQuantity)
  const { openDialog } = useHandleDialog()
  const { unitLabel = '', purchaseLimit } = product
  const hasReachedLimit = Boolean(purchaseLimit) && purchaseLimit === quantity
  const minimumIncrement = getQtyIncrementForProduct(product)
  const hasQuantity = quantity >= minimumIncrement
  const hasMinimumQuantity = quantity === minimumIncrement
  let shoppingContext = 'cart'
  if (currentOrderId) shoppingContext = 'order'
  if (updateListOnly) shoppingContext = 'list'

  const name = getProductCardName(product)
  const [, setSeenNoteTooltip, getCanShowNoteTooltip] = useCanShowViewType('ProductNoteTooltip')
  const [showPopover, setShowPopover] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const { markCartMutationTriggered } = useAutoOpenFreeItemPromoModal({
    promotionMetadata,
    enabled: !updateListOnly,
  })
  const runCommerceGate = useProductCommerceGate()

  useEffect(() => {
    const handleScroll = () => {
      setIsOpen(false)
      window.removeEventListener('scroll', handleScroll)
    }
    if (isOpen && !getCanShowNoteTooltip()) {
      const timeout = setTimeout(() => {
        window.addEventListener('scroll', handleScroll)
      }, 500)
      return () => {
        clearTimeout(timeout)
        window.removeEventListener('scroll', handleScroll)
      }
    }
  }, [isOpen, getCanShowNoteTooltip])

  const setOpenStateTimeout = (ms = 6000) => {
    clearOpenStateTimeout()
    openStateTimeout.current = window.setTimeout(() => {
      setIsOpen(false)
      setShowAddNote(false)
    }, ms)
  }

  useEffect(() => {
    if (isOpen) {
      const onKeyDown = ({ key }: KeyboardEvent) => {
        if (key === keys.ESCAPE) {
          setIsOpen(false)
          addOpenButton.current?.focus()
        }
      }
      window.addEventListener('keydown', onKeyDown)
      return () => {
        window.removeEventListener('keydown', onKeyDown)
      }
    }
  }, [isOpen])

  const clearOpenStateTimeout = () => {
    clearTimeout(openStateTimeout.current)
    openStateTimeout.current = undefined
  }

  const renderOpenIcon = () => {
    if (quantity === 0 || isOpen) {
      return <AddIcon />
    } else {
      return (
        <Body className="quantity-badge" strong size="md" surface="inverse">
          {quantity}
        </Body>
      )
    }
  }

  const renderRemoveIcon = () => {
    if (!hideDeleteIcon && !preventRemoval && (hasMinimumQuantity || !hasQuantity)) {
      return <DeleteIcon />
    } else {
      return <SubtractIcon />
    }
  }

  const getAriaLabel = () => {
    if (!isOpen && hasQuantity) {
      return `Open ${name} quantity selector - current quantity ${quantity} ${unitLabel}`
    }
    return `Add ${minimumIncrement || ''} ${unitLabel} ${name} to ${shoppingContext}`
  }

  const addAndOpenQuantitySelector = () => {
    // A list-only update writes to the list, not the cart, so skip the cart's address gating.
    if (updateListOnly) {
      handleAtc()
      return
    }
    runCommerceGate({ product, onReady: handleAtc })
  }

  const handleAtc = async () => {
    if (product.parent) {
      openDialog(DynamicVariationsQuickAddModal, {
        product,
        listIndex: index,
        onComplementaryShelfFetch,
        listId,
      })
      return
    }
    setIsOpen(true)
    if (!updateListOnly) {
      getCanShowNoteTooltip() && setSeenNoteTooltip()
      window.setTimeout(() => {
        trackElementViewed({
          type: 'tooltip',
          message_goal: 'product_notes_intro',
        })
        setShowAddNote(true)
        setShowPopover(shouldShowPopover && getCanShowNoteTooltip())
      }, 250)
    }
    if (!quantity || isOpen) {
      if (!hasReachedLimit) {
        const shouldShowOutOfStockModal = await onAdd()
        if (!shouldShowOutOfStockModal) {
          markCartMutationTriggered()
        }
      }
      if ('ontouchstart' in document.documentElement) {
        clearOpenStateTimeout()
      }
    }
  }

  const handleRemoveClicked = () => {
    if (quantity > minimumIncrement) {
      onRemove()
      if ('ontouchstart' in document.documentElement) {
        clearOpenStateTimeout()
      }
    }

    if (quantity === minimumIncrement) {
      setIsOpen(false)
      setShowAddNote(false)
      onRemove()
      addOpenButton.current?.focus()
    }
  }

  useEffect(() => {
    return () => {
      clearOpenStateTimeout()
    }
  }, [])

  return (
    <>
      {!!showProductFlag &&
        (hasReachedLimit && isOpen ? (
          <FlagContainer data-show-larger-products={showLargerProducts}>
            <FlagBadge type="limit" limit={purchaseLimit} />
          </FlagContainer>
        ) : (
          <ProductFlag product={product} data-show-larger-products={showLargerProducts} />
        ))}
      <Wrapper
        data-show-add-note={showAddNote}
        data-is-open={isOpen}
        onMouseEnter={() => clearOpenStateTimeout()}
        onMouseLeave={() => !showPopover && setOpenStateTimeout()}
        onFocus={() => clearOpenStateTimeout()}
        onBlur={() => setOpenStateTimeout(1000)}
      >
        <QuickAddBackground isOpen={isOpen}>
          <QuickAddOpenButton
            size="sm"
            $isOpen={isOpen}
            onClick={addAndOpenQuantitySelector}
            ref={addOpenButton}
            icon={renderOpenIcon}
            variant={hasQuantity || isOpen ? 'primary' : 'secondary'}
            surface={isOpen ? 'inverse' : 'default'}
            aria-label={getAriaLabel()}
            aria-describedby={`quantity-in-cart-${product.id} product-quick-add-note-${product.id}`}
            aria-haspopup="true"
            aria-expanded={isOpen}
            aria-controls={`product${product.id}`}
            disabled={orderIsUpdating || (isOpen && hasReachedLimit)}
            data-dtname="product-quantity-quick-add"
          />
        </QuickAddBackground>
        <Menu role="menu" id={`product${product.id}${id}`}>
          <li
            role="menuitem"
            aria-label="Add product note. Do you have any notes for your shopper?"
          >
            {!!showAddNote && !!isOpen && (
              <QuickAddNote product={{ ...product, quantity }} shouldShowPopover={showPopover} />
            )}
          </li>
          <ControlsBackground
            role="menuitem"
            data-is-open={isOpen}
            data-show-add-note={showAddNote}
            aria-hidden={!isOpen}
          >
            {!!isOpen && (
              <ControlsContent className="QuickAddOpen">
                <QuickRemoveButton
                  size="sm"
                  $isOpen={isOpen}
                  onClick={handleRemoveClicked}
                  aria-label={`Remove ${minimumIncrement || ''} ${unitLabel} ${name}`}
                  aria-describedby={`quantity-in-cart-${product.id}`}
                  icon={renderRemoveIcon}
                  surface="inverse"
                  disabled={
                    orderIsUpdating || !hasQuantity || (preventRemoval && hasMinimumQuantity)
                  }
                />
                {orderIsUpdating ? (
                  <LoadingIndicator size="sm" surface="inverse" />
                ) : (
                  <div
                    tabIndex={0}
                    aria-label={`${quantity} ${
                      unitLabel ? normalizeUnitLabel(product, quantity) : `in ${shoppingContext}`
                    }`}
                    id={`quantity-in-cart-${product.id}`}
                  >
                    <Body strong surface="inverse" size="md" as="span" aria-hidden>
                      {quantity}
                    </Body>
                    {!!unitLabel && (
                      <ContextCopy as="span" size="sm" surface="inverse" align="center" aria-hidden>
                        {getUnitLabel(product, quantity)}
                      </ContextCopy>
                    )}
                    {!unitLabel && !!displayContext && (
                      <ContextCopy as="span" size="sm" surface="inverse" aria-hidden>
                        {updateListOnly ? 'ct' : `in ${shoppingContext}`}
                      </ContextCopy>
                    )}
                  </div>
                )}
              </ControlsContent>
            )}
          </ControlsBackground>
        </Menu>
      </Wrapper>
    </>
  )
}

const FlagContainerStyles = css`
  position: absolute;
  z-index: 1;
  top: -6.25rem;
  left: 0;
`

const FlagContainer = styled.div<{
  'data-show-larger-products': boolean
}>`
  ${FlagContainerStyles}

  @media ${screenSizes.tablet} {
    &[data-show-larger-products='true'] {
      top: -9rem;
    }
  }
`

const ProductFlag = styled(ProductCardFlag)<{
  'data-show-larger-products': boolean
}>`
  ${FlagContainerStyles}

  @media ${screenSizes.tablet} {
    &[data-show-larger-products='true'] {
      top: -9rem;
    }
  }
`

const Wrapper = styled.div<{
  'data-show-add-note': boolean
  'data-is-open': boolean
}>`
  /* Default (closed) state */
  pointer-events: none;

  /* Open state */
  &[data-is-open='true'] {
    pointer-events: all;
  }

  /* Show note and open state */
  &[data-show-add-note='true'][data-is-open='true'] {
    --shadow-fill: ${({ theme }) => theme.shadows[1]};
    animation: ${keyframes.shadowFill} ease 0.25s;
    animation-fill-mode: forwards;
    border-radius: 1.125rem;
  }
`

const QuickAddBackground = styled.div<{ isOpen: boolean }>`
  position: absolute;
  right: 0;
  bottom: 0;
  background-color: ${({ theme }) => theme.color.background.base.neutral.subtle_00};
  border-radius: 25rem;
  margin: ${({ isOpen }) => (isOpen ? '2px' : 0)};
`

const QuickRemoveButton = styled(IconButton)<{ $isOpen: boolean }>`
  position: absolute;
  left: 0;
  bottom: 0;
  margin: 2px;

  &::after {
    content: '';
    position: absolute;
    inset: -2px;
  }
`

const QuickAddOpenButton = styled(IconButton)<{ $isOpen: boolean }>`
  pointer-events: all;

  .quantity-badge {
    line-height: ${({ theme }) => theme.iconButton.icon.sm.height}px;
    min-width: ${({ theme }) => theme.iconButton.icon.sm.width}px;
  }

  &::after {
    content: '';
    position: absolute;
    inset: -2px;
  }
`

const QuickAddNote = styled(ProductQuickAddNote)`
  display: block;
  position: relative;
  margin-top: -2rem;
  border-radius: 1.125rem 1.125rem 0 0;
  background: ${({ theme }) => theme.color.background.base.brand.primary};
  opacity: 1;
  animation: ${keyframes.slideUp} ease 0.25s;
`

const Menu = styled.ul`
  flex-direction: row-reverse;
  list-style: none;
  margin: 0;
  padding: 0;
`

const ControlsContent = styled.div`
  display: flex;
`

const ControlsBackground = styled.li<{
  'data-show-add-note': boolean
  'data-is-open': boolean
}>`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  color: ${({ theme }) => theme.primaryContrast};
  border-radius: 1.125rem;
  background: ${({ theme }) => theme.color.background.base.brand.primary};
  overflow: hidden;
  width: 100%;
  transition: opacity 0.25s ease;
  opacity: 0;
  transform-origin: right;
  backface-visibility: hidden;
  cursor: default;
  height: 2.25rem;

  /* Not showing note - box shadow only applies when false */
  &:not([data-show-add-note='true']) {
    box-shadow: ${({ theme }) => theme.shadows[1]};
  }

  /* Showing note - different border radius and transition */
  &[data-show-add-note='true'] {
    border-radius: 0 0 1.125rem 1.125rem;
    transition: border-radius 0.25s;
  }

  /* Closed state - collapse animation */
  &:not([data-is-open='true']) {
    animation: ${keyframes.collapse} ease 0.25s;
  }

  /* Open state - expand animation and opacity */
  &[data-is-open='true'] {
    opacity: 1;
    animation: ${keyframes.expand} ease 0.25s;
  }
`

const ContextCopy = styled(Body)`
  &::before {
    content: ' ';
  }
`
